"use client";

import { useEffect, useState } from "react";
import { Menu, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-5 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-flex items-center px-3 py-2 text-[13px] uppercase tracking-[0.12em] text-foreground/70 hover:text-foreground hover:bg-transparent focus:bg-transparent transition-colors duration-300">
                <span className="relative font-default">
                  {item.label}
                  <span className="absolute -bottom-1 left-0 h-px w-0 bg-primary transition-all duration-300 group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-8 pt-12">
        <div className="h-10 w-3/4 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-2/3 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-1/2 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-2 px-8 pt-16">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onItemClick} className="group flex items-baseline gap-4 border-b border-border/50 py-5 text-foreground hover:text-primary transition-colors duration-300">
          <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-mono">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span className="font-default text-3xl font-light leading-none">
            {item.label}
          </span>
        </Link>)}
    </nav>;
}
export default function StudioThorneHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="5099" id="studio-thorne-header" className={`sticky top-0 z-50 w-full transition-all duration-[400ms] ease-out bg-background text-foreground ${scrolled ? "bg-background/90 backdrop-blur-sm border-b border-border" : "bg-background border-b border-transparent"}`}>
      <div className="mx-auto flex w-full max-w-[1400px] items-center justify-between px-6 py-5 md:px-8 md:py-6">
        <div className="flex items-center">
          <Link to="/" className="inline-flex items-center" aria-label="Studio Thorne home">
            <WvcLogo className="h-7 w-auto" />
          </Link>
        </div>

        <div className="hidden lg:flex flex-1 justify-center">
          <MenuProvider menu_id="2" className="header-menu">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="flex items-center gap-3 md:gap-4">
          <Button asChild className="hidden sm:inline-flex h-10 rounded-lg bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground px-5 text-[12px] uppercase tracking-[0.14em] font-default shadow-md">
            <Link to="/InquiriesPage">
              <span className="relative flex items-center gap-2.5">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-lg bg-primary-foreground opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-lg bg-primary-foreground" />
                </span>
                Inquire Now
              </span>
            </Link>
          </Button>

          <div className="hidden md:flex items-center gap-4">
            <Separator orientation="vertical" className="h-6 bg-border" />
            <Link to="tel:+14155550199" className="inline-flex items-center gap-2 text-[12px] tracking-[0.08em] text-muted-foreground hover:text-foreground transition-colors duration-300">
              <Phone className="h-3.5 w-3.5" />
              <span className="font-mono">(415) 555-0199</span>
            </Link>
          </div>

          <div className="lg:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-10 w-10 text-foreground hover:bg-accent" aria-label="Open menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-md bg-background border-l border-border p-0">
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between px-8 pt-6">
                    <Link to="/" onClick={() => setMobileOpen(false)} aria-label="Studio Thorne home">
                      <WvcLogo className="h-7 w-auto" />
                    </Link>
                  </div>

                  <MenuProvider menu_id="2" className="header-menu flex-1 overflow-y-auto">
                    <MobileMenuConsumer onItemClick={() => setMobileOpen(false)} />
                  </MenuProvider>

                  <div className="px-8 pb-10 pt-6 border-t border-border space-y-4">
                    <Button asChild className="w-full h-12 rounded-lg bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground text-[12px] uppercase tracking-[0.14em] font-default">
                      <Link to="/InquiriesPage" onClick={() => setMobileOpen(false)}>
                        <span className="flex items-center gap-2.5">
                          <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-lg bg-primary-foreground opacity-60" />
                            <span className="relative inline-flex h-2 w-2 rounded-lg bg-primary-foreground" />
                          </span>
                          Inquire Now
                        </span>
                      </Link>
                    </Button>
                    <Link to="tel:+14155550199" onClick={() => setMobileOpen(false)} className="flex items-center justify-center gap-2 text-[12px] tracking-[0.08em] text-muted-foreground hover:text-foreground transition-colors">
                      <Phone className="h-3.5 w-3.5" />
                      <span className="font-mono">(415) 555-0199</span>
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}